import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTourStore } from "@/lib/stores/tour-store";
import { cn } from "@/lib/utils";

interface TourStep {
  id: string;
  title: string;
  body: string;
  anchor: string | null;
  side: "top" | "bottom" | "left" | "right";
}

const TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to Rayloop!",
    body: "Let's take a quick tour to show you around.",
    anchor: null,
    side: "bottom",
  },
  {
    id: "prompt",
    title: "Create with AI",
    body: "Describe what you want to create and our AI agent will build a workflow for you.",
    anchor: "prompt-input",
    side: "bottom",
  },
  {
    id: "templates",
    title: "Start from a template",
    body: "Or pick a pre-built template to get going faster.",
    anchor: "templates",
    side: "top",
  },
  {
    id: "sidebar",
    title: "Your workspace",
    body: "Access your projects, assets, and settings from the sidebar.",
    anchor: "sidebar",
    side: "right",
  },
  {
    id: "done",
    title: "You're all set!",
    body: "Try describing a workflow to get started.",
    anchor: null,
    side: "bottom",
  },
];

function getAnchorRect(anchor: string | null): DOMRect | null {
  if (!anchor) return null;
  const el = document.querySelector(`[data-tour="${anchor}"]`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

function getTooltipPosition(
  rect: DOMRect | null,
  side: TourStep["side"],
): React.CSSProperties {
  // Centered card for steps without an anchor — use inset+margin
  // instead of transform so Framer Motion's y animation doesn't override it
  if (!rect) {
    return {
      position: "fixed",
      top: "5%",
      left: 0,
      right: 0,
      margin: "0 auto",
      height: "fit-content",
      width: "fit-content",
    };
  }

  const gap = 12;
  const style: React.CSSProperties = { position: "fixed" };

  switch (side) {
    case "bottom":
      style.top = rect.bottom + gap;
      style.left = rect.left + rect.width / 2;
      style.transform = "translateX(-50%)";
      break;
    case "top":
      style.bottom = window.innerHeight - rect.top + gap;
      style.left = rect.left + rect.width / 2;
      style.transform = "translateX(-50%)";
      break;
    case "right":
      style.top = rect.top + rect.height / 2;
      style.left = rect.right + gap;
      style.transform = "translateY(-50%)";
      break;
    case "left":
      style.top = rect.top + rect.height / 2;
      style.right = window.innerWidth - rect.left + gap;
      style.transform = "translateY(-50%)";
      break;
  }

  return style;
}

function buildClipPath(rect: DOMRect | null): string {
  if (!rect) return "";
  const pad = 8;
  const t = Math.max(0, rect.top - pad);
  const l = Math.max(0, rect.left - pad);
  const b = rect.bottom + pad;
  const r = rect.right + pad;
  const rad = 24; // match element border-radius (16px) + padding (8px)
  const W = window.innerWidth;
  const H = window.innerHeight;

  // SVG path: outer rect (CW) + inner rounded-rect (CCW) for evenodd cutout
  return `path(evenodd, "M0 0 H${W} V${H} H0 Z M${l + rad} ${t} H${r - rad} A${rad} ${rad} 0 0 1 ${r} ${t + rad} V${b - rad} A${rad} ${rad} 0 0 1 ${r - rad} ${b} H${l + rad} A${rad} ${rad} 0 0 1 ${l} ${b - rad} V${t + rad} A${rad} ${rad} 0 0 1 ${l + rad} ${t} Z")`;
}

interface DashboardTourProps {
  onComplete?: (skipped: boolean) => void;
}

export function DashboardTour({ onComplete }: DashboardTourProps) {
  const active = useTourStore((s) => s.active);
  const stepIndex = useTourStore((s) => s.stepIndex);
  const next = useTourStore((s) => s.next);
  const back = useTourStore((s) => s.back);
  const skipTour = useTourStore((s) => s.skip);

  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const step = TOUR_STEPS[stepIndex];

  const measureAnchor = useCallback(() => {
    setAnchorRect(getAnchorRect(step?.anchor ?? null));
  }, [step?.anchor]);

  // Re-measure on step change and window resize
  useEffect(() => {
    if (!active) return;
    // Defer initial measurement to avoid synchronous setState in effect
    const raf = requestAnimationFrame(measureAnchor);
    window.addEventListener("resize", measureAnchor);
    window.addEventListener("scroll", measureAnchor, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measureAnchor);
      window.removeEventListener("scroll", measureAnchor, true);
    };
  }, [active, measureAnchor]);

  const handleSkip = useCallback(() => {
    skipTour();
    onComplete?.(true);
  }, [skipTour, onComplete]);

  const handleFinish = useCallback(() => {
    skipTour();
    onComplete?.(false);
  }, [skipTour, onComplete]);

  // Handle "done" step CTA — focus the prompt textarea
  const handleDoneCta = useCallback(() => {
    handleFinish();
    // Small delay so the tour unmounts before we try to focus
    requestAnimationFrame(() => {
      const textarea = document.querySelector<HTMLTextAreaElement>(
        '[data-tour="prompt-input"] textarea',
      );
      textarea?.focus();
    });
  }, [handleFinish]);

  if (!active || !step) return null;

  const clipPath = buildClipPath(anchorRect);
  const tooltipStyle = getTooltipPosition(anchorRect, step.side);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === TOUR_STEPS.length - 1;
  const totalSteps = TOUR_STEPS.length;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-[100] bg-black/40 transition-[clip-path] duration-300"
        style={clipPath ? { clipPath } : undefined}
        onClick={handleSkip}
      />

      {/* Tooltip popover */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="bg-popover ring-foreground/10 fixed z-[101] w-80 rounded-lg p-5 shadow-md ring-1"
          style={tooltipStyle}
        >
          <h3 className="text-foreground font-display text-lg font-normal">
            {step.title}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">{step.body}</p>

          <div className="mt-4 flex items-center justify-between">
            {/* Left side: step counter + back */}
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground text-xs">
                {stepIndex + 1} of {totalSteps}
              </span>
              {!isFirst && !isLast && (
                <button
                  onClick={back}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors"
                >
                  Back
                </button>
              )}
            </div>

            {/* Right side: skip + next/CTA */}
            <div className="flex items-center gap-3">
              {!isLast && (
                <button
                  onClick={handleSkip}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors"
                >
                  Skip tour
                </button>
              )}

              {isLast ? (
                <button
                  onClick={handleDoneCta}
                  className={cn(
                    "rounded-lg px-4 py-1.5 text-sm font-medium transition-colors",
                    "bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90",
                  )}
                >
                  Get started
                </button>
              ) : (
                <button
                  onClick={next}
                  className={cn(
                    "rounded-lg px-4 py-1.5 text-sm font-medium transition-colors",
                    "bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90",
                  )}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}
